import React, { useMemo } from 'react';
import parse from 'html-react-parser';
import { Icon } from '../../../../../../base/components/Icon';
import { Location } from '../../../../../../base/components/Location';
import { cutAgeToFirstLetter } from '../../../../../../base/utils';
import { WorkerData as WorkerDataProps } from '../../../api/interfaces';

import './styles.scss';
import { Gender, OnlineStatus } from '../../../../../../base/enums';
import { HoverTooltip } from '../../../../../../base/components/HoverTooltip';
import { __t } from '../../../../../../base/utils/t';
import { useIsMobile } from '../../../../../../base/hooks';

interface Props {
    workerData: Pick<
        WorkerDataProps,
        'image' | 'gender' | 'name' | 'age' | 'location' | 'citizenship' | 'url' | 'online' | 'isDeleted'
    >;
    isQuickResponse: boolean;
    markNewResponseAsReadHandler: () => void;
}

export const WorkerData = ({
    workerData: { image, gender, online, citizenship, name, age, location, url, isDeleted },
    isQuickResponse,
    markNewResponseAsReadHandler,
}: Props) => {
    const isMobile = useIsMobile();

    const photo = useMemo(() => {
        if (isDeleted) {
            return (
                <Icon
                    icon='user-slash-duonote'
                    size='24px'
                    className='responses-table-worker-data__user-deleted-icon'
                />
            );
        }

        if(isQuickResponse) {
            return (
                <Icon
                    icon='circle-bolt-duonote'
                    size='48px'
                    color='purple'
                    className='responses-table-worker-data__quick-response-icon'
                />
            );
        }

        if (!image) {
            switch (gender) {
                case Gender.Male:
                    return (
                        <Icon
                            icon='user-vneck-hair-duonote'
                            size='24px'
                            className='responses-table-worker-data__photo-placeholder'
                        />
                    );
                case Gender.Female:
                    return (
                        <Icon
                            icon='user-vneck-hair-long-duonote'
                            size='24px'
                            className='responses-table-worker-data__photo-placeholder'
                        />
                    );
                default:
                    return null;
            }
        }

        return <img src={image} className='responses-table-worker-data__photo' />;
    }, [image, gender, isDeleted]);

    const onlineIcon = useMemo(() => {
        if (isDeleted) {
            return null;
        }

        switch (online) {
            case OnlineStatus.Online:
                return (
                    <div className='responses-table-worker-data__online-status-icon responses-table-worker-data__online-status-icon--online'></div>
                );
            case OnlineStatus.Offline:
            default:
                return <div className='responses-table-worker-data__online-status-icon'></div>;
        }
    }, [online, isDeleted]);

    const citizenshipFlag = useMemo(() => {
        if (!citizenship?.country || isDeleted) {
            return null;
        }

        return (
            <div className='responses-table-worker-data__citizenship'>
                <HoverTooltip
                    align={isMobile ? 'left' : 'center'}
                    text={
                        <>
                            <span className='responses-table-worker-data__hover-tooltip-title'>
                                {__t('Громадянство')}:&nbsp;
                            </span>
                            {citizenship.country.name}
                        </>
                    }
                >
                    {parse(citizenship.country.flag)}
                </HoverTooltip>
            </div>
        );
    }, [citizenship, isDeleted]);

    return (
        <div className='responses-table-worker-data'>
            <div className='responses-table-worker-data__photo-block'>
                {onlineIcon}
                <div className='responses-table-worker-data__photo-wrap'>{photo}</div>
                {citizenshipFlag}
            </div>

            <div className='responses-table-worker-data__content'>
                {isDeleted ? (
                    <span className='responses-table-worker-data__user-deleted'>{__t('Удален')}</span>
                ) : (
                    <>
                        <div className='responses-table-worker-data__name-age'>
                            {url ? (
                                <a
                                    href={url}
                                    target='_blank'
                                    className='responses-table-worker-data__name responses-table-worker-data__name--link'
                                    rel='noreferrer'
                                    title={name}
                                    onClick={markNewResponseAsReadHandler}
                                >
                                    {name}
                                </a>
                            ) : (
                                <div className='responses-table-worker-data__name'>{name}</div>
                            )}
                            <span className='responses-table-worker-data__age'>
                                &nbsp;·&nbsp;{cutAgeToFirstLetter(age)}
                            </span>
                        </div>
                        {location && (
                            <Location
                                flag={location.country?.flag || ''}
                                city={location.city?.name || location.country.name}
                                className='responses-table-worker-data__location'
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
